import { ref, computed, watchEffect, toRef } from 'vue'
import { useStormStore } from '@/stores/StormStore'
import { useMapDrawingStore } from '@/stores/MapDrawingStore'
import { useSheetController } from '@/composables/useSheetsController'
import useSettingsGlobals from '@/composables/useSettingsGlobals'

export const useStormLayers = (mapReady, region) => {
  const mapReadyState = toRef(mapReady);
  const stormLayersLoaded = ref(false);

  const { $WEB_VIEW } = useSettingsGlobals()

  const stormStore = useStormStore()
  const mapDrawingStore = useMapDrawingStore()

  const stormLayers = {}
  const allowKMLClick = ref(true)
  const allowKMLClickTimeout = ref(null)
  const zoomLevel = ref(null);

  const activeStorms = computed(() => stormStore.activeStorms.length > 0)
  const kmlLayerType = computed(() => stormStore.plotType === 'cone' ? 'forecast' : zoomLevel.value < 6 ? 'spaghettiLines' : 'spaghetti' )
  const kmlLayerGroupType = computed(() => stormStore.plotType === 'cone' ? 'forecast' : 'spaghetti' )

  function createStormLayers() {
    stormStore.activeStorms.forEach((storm) => {
      if (!storm || stormLayers[storm.id]) return

      stormLayers[storm.id] = {}

      if (storm.forecast) {
        const kmlLayerForecast = new google.maps.KmlLayer(storm.forecast.forecastkml, {
          preserveViewport: true,
          suppressInfoWindows: true
        })

        stormLayers[storm.id].forecast = {
          kmlLayer: kmlLayerForecast,
          listener: google.maps.event.addListener(
            kmlLayerForecast,
            'click',
            stormLayerClickHandler(storm.id)
          )
        }
      }

      if (storm.spaghetti) {
        const kmlLayerSpaghetti = new google.maps.KmlLayer(storm.spaghetti.adeckkml, {
          preserveViewport: true,
          suppressInfoWindows: true
        })
        const kmlLinesLayerSpaghetti = new google.maps.KmlLayer(storm.spaghetti.adeckkmllines, {
          preserveViewport: true,
          suppressInfoWindows: true
        })

        stormLayers[storm.id].spaghetti = {
          kmlLayer: kmlLayerSpaghetti,
          listener: google.maps.event.addListener(
            kmlLayerSpaghetti,
            'click',
            stormLayerClickHandler(storm.id)
          )
        }

        stormLayers[storm.id].spaghettiLines = {
          kmlLayer: kmlLinesLayerSpaghetti,
          listener: google.maps.event.addListener(
            kmlLinesLayerSpaghetti,
            'click',
            stormLayerClickHandler(storm.id)
          )
        }
      }

      const nhcKMZUrl = `http://stormtrack.weatherflow.com/wwa/${storm.id.toUpperCase()}_${storm.advisory.advisorynumber.padStart(storm.advisory.advisorynumber.length + 1, '0')}adv_WW.kml`
      const kmlNHCLayer = new google.maps.KmlLayer(nhcKMZUrl, {
        preserveViewport: true,
        suppressInfoWindows: true
      })

      stormLayers[storm.id].nhc = {
        kmlLayer: kmlNHCLayer,
        listener: google.maps.event.addListener(
          kmlNHCLayer,
          'click',
          stormLayerClickHandler(storm.id, 'nhc')
        )
      }
    })
  }

  function stormLayerClickHandler(stormId, otherLayerType) {
    return (kmlEvent) => {
      if (!allowKMLClick.value) return

      if (mapDrawingStore.mode === 'editing') {
        google.maps.event.trigger(window.$JWX.map, 'click', kmlEvent)
        return
      }

      const id = kmlEvent.featureData.id
      const layerType = otherLayerType ? otherLayerType : kmlLayerType.value

      layerType === 'nhc'
        ? stormStore.convertNHCAdviosryToObject(stormId, kmlEvent.featureData)
        : stormStore.convertPointForecastToObject(stormId, id, layerType)
      useSheetController.toggleSheet('info', 'storm-forecast', true)
    }
  }

  function updateStormLayers() {
    if (stormStore.visibleStorms.size === 1) {
      if (!Object.hasOwnProperty.call(stormStore.selectedStorm, kmlLayerGroupType.value)) {
        stormStore.setPlotType(stormStore.plotType === 'cone' ? 'spaghetti' : 'cone')
      }
    }    
    
    const previousPlotType = stormStore.plotType === 'cone' ? 'spaghetti' : 'cone'
    const previousKmlLayerType = previousPlotType === 'cone' ? 'forecast' : 'spaghetti'

    const unusedSpaghettiKmlLayerType =
      stormStore.plotType === 'cone'
        ? null
        : kmlLayerType.value === 'spaghetti'
        ? 'spaghettiLines'
        : 'spaghetti';

    Object.keys(stormLayers).forEach((stormId) => {

      if ( stormStore.activeStorms.find((storm) => storm.id === stormId) && stormLayers[stormId][kmlLayerType.value] ) {
        if (stormStore.visibleStorms.has(stormId)) {
          stormLayers[stormId][kmlLayerType.value].kmlLayer.setMap(window.$JWX.map)         
        } else {
          stormLayers[stormId][kmlLayerType.value].kmlLayer.setMap(null)
        }
      }

      if (
        stormStore.plotType === 'spaghetti' &&
        stormLayers[stormId][unusedSpaghettiKmlLayerType]
      ) {
        stormLayers[stormId][unusedSpaghettiKmlLayerType].kmlLayer.setMap(null)
      }

      if (previousPlotType === 'spaghetti') {
        if (stormLayers[stormId]['spaghetti'])
          stormLayers[stormId]['spaghetti'].kmlLayer.setMap(null)
        if (stormLayers[stormId]['spaghettiLines'])
          stormLayers[stormId]['spaghettiLines'].kmlLayer.setMap(null)
      }

      if (stormLayers[stormId][previousKmlLayerType]) stormLayers[stormId][previousKmlLayerType].kmlLayer.setMap(null);

      if (stormStore.selectedStorm.id === stormId) {
        stormLayers[stormId].nhc.kmlLayer.setMap(window.$JWX.map)
      } else {
        stormLayers[stormId].nhc.kmlLayer.setMap(null)
      }      
    })

    stormStore.checkSelectedStorm();

  }

  function initStormLayers() {
    zoomLevel.value = window.$JWX.map.getZoom();

    createStormLayers();
    
    watchEffect(() => {
      activeStorms.value &&
      stormStore.visibleStorms.size > -1 &&
      stormStore.plotType &&
      updateStormLayers();
    })

    if ($WEB_VIEW) {
      google.maps.event.addListener(window.$JWX.map, 'click', function (e) {
        window.$JWX.cosmos.closeQuickView()
      })
    }

    google.maps.event.addListener(window.$JWX.map, 'infowindow_clicked', function (e) {
      allowKMLClick.value = false

      clearTimeout(allowKMLClickTimeout.value)

      allowKMLClickTimeout.value = setTimeout(() => {
        allowKMLClick.value = true
      }, 1000)
    })

    google.maps.event.addListener(window.$JWX.map, 'zoom_changed', function (e) {
      zoomLevel.value = window.$JWX.map.getZoom();

      updateStormLayers();
    })

    stormLayersLoaded.value = true;
    if (!region) window.$JWX.map.fitBounds(stormStore.selectedStorm[kmlLayerGroupType.value].bounds)
  }

  watchEffect(() => {
    if (!mapReadyState.value || stormLayersLoaded.value) return;

    initStormLayers();
  })
}